import type { ButtonHTMLAttributes, ReactNode } from 'react';

import type { LucideIcon } from 'lucide-react';

import { cn } from '@/utils/cn';

/**
 * BrandButton — the Labs primary CTA, fs-* native (Inter, 18px, medium).
 *
 * Variants:
 *   - `primary` (default): bg-fs-primary / text-fs-on-primary, hover dims opacity.
 *   - `outline`: brand border + text-fs-brand on transparent, hover fs-brand-subtle wash (the "Back" button).
 *
 * Spacing/type unified on the Figma values: px-6/py-4 (=== px-fs-lg/py-fs-md = 24/16) · text-[18px] · icons h-5 w-5.
 */
export function BrandButton({
  children,
  variant = 'primary',
  icon: Icon,
  iconPosition = 'trailing',
  className,
  type = 'button',
  ...props
}: {
  children: ReactNode;
  variant?: 'primary' | 'outline';
  icon?: LucideIcon;
  iconPosition?: 'leading' | 'trailing';
} & ButtonHTMLAttributes<HTMLButtonElement>): ReactNode {
  const iconEl = Icon ? <Icon className="h-5 w-5" strokeWidth={2} /> : null;

  return (
    <button
      className={cn(
        // `border border-transparent` reserves the SAME 1px box as the bordered secondary
        // BackButton, so primary + Back render at identical heights (the border-box border still
        // contributes to an auto height). The primary variant keeps a transparent border; the
        // outline variant overrides the color.
        'inline-flex cursor-pointer items-center justify-center gap-2 rounded-fs-button border border-transparent px-6 py-4 font-medium text-[18px] leading-none transition-colors disabled:cursor-default',
        variant === 'primary'
          ? 'bg-fs-primary text-fs-on-primary transition-opacity hover:opacity-90'
          : 'border-fs-primary text-fs-brand hover:bg-fs-brand-subtle',
        className,
      )}
      type={type}
      {...props}>
      {iconPosition === 'leading' ? iconEl : null}
      {children}
      {iconPosition === 'trailing' ? iconEl : null}
    </button>
  );
}
